// src/layouts/subscription-plans/index.js

import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Dialog components
import EditDialog from "components/Dialogs/EditDialog";

// Controllers
import {
  getSubscriptionPlans,
  updateSubscriptionPlan,
} from "data/controllers/subscriptionPlansController";

function SubscriptionPlans() {
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({});


  const tabs = [
    { label: "All Plans", value: "all" },
    { label: "Candidate Plans", value: "candidate" },
    { label: "Recruiter Plans", value: "recruiter" },
  ];

  const columns = [
    { Header: "plan", accessor: "plan", width: "25%", align: "left" },
    { Header: "target", accessor: "target", align: "center" },
    { Header: "price", accessor: "price", align: "center" },
    { Header: "features", accessor: "features", width: "30%", align: "left" },
    { Header: "status", accessor: "status", align: "center" },
    { Header: "action", accessor: "action", align: "center" },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    const tabValue = tabs[selectedTab].value;
    if (tabValue === "all") {
      setFilteredPlans(plans);
    } else {
      setFilteredPlans(plans.filter((plan) => plan.targetUserType === tabValue));
    }
  }, [plans, selectedTab]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getSubscriptionPlans();
      setPlans(response.data);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  };



  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };



  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setFormData(getFormData(plan));
    setEditDialogOpen(true);
  };

  const handleSavePlan = async (submitFormData) => {
    try {
      const response = await updateSubscriptionPlan(selectedPlan._id, submitFormData);
      setPlans(plans.map((plan) => (plan._id === selectedPlan._id ? response : plan)));
      setEditDialogOpen(false);
      setSelectedPlan(null);
      setFormData({});
    } catch (error) {
      console.error("Error saving plan:", error);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return "Free";
    return `₹${(price / 100).toLocaleString("en-IN")}`;
  };

  const getEditableFields = (targetUserType) => {
    const commonFields = [
      { label: "Plan Name", key: "name", type: "text", required: true, disabled: true },
      { label: "Target User Type", key: "targetUserType", type: "text", required: true, disabled: true },
      { label: "Price (in paise)", key: "price", type: "number", required: true },
      { label: "Discount (%)", key: "discount", type: "number", required: false },
      { label: "Level", key: "level", type: "number", required: true, disabled: true },
      { label: "Plan Duration (days)", key: "planDurationDays", type: "number", required: true },
      {
        label: "AI Cache Limit (minutes)",
        key: "aiCacheLimit",
        type: "select",
        options: [0, 60, 120, 180, 300],
        required: true,
      },
      { label: "Is Recommended", key: "isRecommended", type: "checkbox", required: false },
      { label: "Crown Badge", key: "crown", type: "checkbox", required: false },
      { label: "Features (comma-separated)", key: "features", type: "textarea", required: false },
    ];

    const candidateFields = [
      { label: "Daily Apply Limit", key: "dailyApplyLimit", type: "number", required: true },
    ];

    const recruiterFields = [
      { label: "Free Resumes", key: "freeResumes", type: "number", required: true },
      { label: "Free Posts", key: "freePosts", type: "number", required: true },
      { label: "Per Day Resume Limit", key: "perDayResumeLimit", type: "number", required: true },
    ];

    if (targetUserType === "candidate") {
      return [...commonFields, ...candidateFields];
    } else if (targetUserType === "recruiter") {
      return [...commonFields, ...recruiterFields];
    } else {
      return commonFields;
    }
  };

  const getFormData = (plan) => {
    if (!plan) {
      return {
        name: "",
        targetUserType: "candidate",
        price: 0,
        discount: 0,
        level: 1,
        freeResumes: 0,
        freePosts: 0,
        dailyApplyLimit: 0,
        perDayResumeLimit: 0,
        planDurationDays: 30,
        aiCacheLimit: 180,
        isRecommended: false,
        crown: false,
        features: "",
      };
    }

    return {
      name: plan.name || "",
      targetUserType: plan.targetUserType || "candidate",
      price: plan.price || 0,
      discount: plan.discount || 0,
      level: plan.level || 1,
      freeResumes: plan.freeResumes || 0,
      freePosts: plan.freePosts || 0,
      dailyApplyLimit: plan.dailyApplyLimit || 0,
      perDayResumeLimit: plan.perDayResumeLimit || 0,
      planDurationDays: plan.planDurationDays || 30,
      aiCacheLimit: plan.aiCacheLimit || 180,
      isRecommended: plan.isRecommended || false,
      crown: plan.crown || false,
      features: Array.isArray(plan.features) ? plan.features.join(", ") : "",
    };
  };

  const processFormData = (formData) => {
    return {
      ...formData,
      freeResumes: parseInt(formData.freeResumes) || 0,
      freePosts: parseInt(formData.freePosts) || 0,
      dailyApplyLimit: parseInt(formData.dailyApplyLimit) || 0,
      perDayResumeLimit: parseInt(formData.perDayResumeLimit) || 0,
      planDurationDays: parseInt(formData.planDurationDays) || 30,
      aiCacheLimit: parseInt(formData.aiCacheLimit) || 180,
      features: formData.features ? formData.features.split(",").map((f) => f.trim()) : [],
    };
  };

  const rows = filteredPlans.map((plan) => ({
    plan: (
      <MDBox>
        <MDTypography variant="button" fontWeight="medium">
          {plan.name}
        </MDTypography>
        {plan.isRecommended && (
          <Chip label="Recommended" color="primary" size="small" sx={{ ml: 1 }} />
        )}
        {plan.crown && <Chip label="Crown" color="warning" size="small" sx={{ ml: 1 }} />}
      </MDBox>
    ),
    target: (
      <Chip
        label={plan.targetUserType}
        color={plan.targetUserType === "candidate" ? "info" : "success"}
        variant="outlined"
      />
    ),
    price: (
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {formatPrice(plan.price)}
        {plan.discount > 0 && (
          <Chip label={`${plan.discount}% OFF`} color="error" size="small" sx={{ ml: 1 }} />
        )}
      </MDTypography>
    ),
    features: (
      <MDBox>
        {plan.features.slice(0, 2).map((feature, index) => (
          <MDTypography key={index} variant="caption" color="text" display="block">
            • {feature}
          </MDTypography>
        ))}
        {plan.features.length > 2 && (
          <MDTypography variant="caption" color="info" fontWeight="medium">
            +{plan.features.length - 2} more
          </MDTypography>
        )}
      </MDBox>
    ),
    status: (
      <Chip
        label={plan.price === 0 ? "Free" : "Paid"}
        color={plan.price === 0 ? "success" : "primary"}
        variant="outlined"
      />
    ),
    action: (
      <MDBox display="flex" alignItems="center" gap={1}>
        <MDTypography
          variant="caption"
          color="info"
          fontWeight="medium"
          sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
          onClick={() => handleEditPlan(plan)}
        >
          Edit
        </MDTypography>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Subscription Plans Management
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={3}>
                <Box
                  sx={{
                    backgroundColor: "#f8f9fa",
                    borderRadius: "12px",
                    padding: "4px",
                    marginBottom: "24px",
                  }}
                >
                  <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    sx={{
                      "& .MuiTabs-indicator": {
                        height: "100%",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "all 300ms ease",
                      },
                      "& .MuiTab-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "14px",
                        minHeight: "40px",
                        borderRadius: "8px",
                        margin: "0 2px",
                        color: "#6c757d",
                        "&.Mui-selected": {
                          color: "#495057",
                          fontWeight: 600,
                        },
                      },
                    }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab key={index} label={tab.label} />
                    ))}
                  </Tabs>
                </Box>
              </MDBox>
              <MDBox px={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Edit Plan Dialog */}
      {editDialogOpen && selectedPlan && (
        <EditDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedPlan(null);
            setFormData({});
          }}
          onSubmit={(_) => handleSavePlan(processFormData(formData))}
          title={`Edit Plan: ${selectedPlan?.name}`}
          fields={getEditableFields(selectedPlan?.targetUserType || "candidate")}
          formData={formData}
          setFormData={setFormData}
        />
      )}


    </DashboardLayout>
  );
}

export default SubscriptionPlans;
